import React, { useState, useEffect, useRef, useMemo } from "react";
import { Search } from "lucide-react";
import { useFetchApi } from "../../../hooks/useFetchApi";
import { StarIcon } from "../../../components//StarIcon";
import {
  getExchangeInfo,
  marketLogoMap,
  STOCK_EXCHANGES,
} from "../../../helper";

interface StockSearchModalProps {
  loading?: boolean;
  selectedStocks: any[];
  setSelectedSotcks: (value: any[]) => void;
  getWatchlist: any;
  watchlistResData: any[];
}

export const StockSelectOptions: React.FC<StockSearchModalProps> = ({
  loading = false,
  selectedStocks,
  setSelectedSotcks,
  getWatchlist,
  watchlistResData,
}) => {
  const [stocks, setStocks] = useState<any[]>([]);
  const [filteredStocks, setFilteredStocks] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExchange, setSelectedExchange] = useState<string>("all");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    response,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  const {
    response: watchlist,
    loading: watchlistLoading,
    execute: updateWatchlist,
  } = useFetchApi("/api/watchlist", {}, "PUT", () => {
    getWatchlist();
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    getStocks();
  }, []);

  useEffect(() => {
    if (response && Object.keys(response).length) {
      const stocksFromExchanges = STOCK_EXCHANGES?.flatMap(
        (exchange) =>
          (response &&
            response?.find((section: any) => section?.section === exchange)
              ?.options) ||
          [],
      );

      const activeStocks = stocksFromExchanges.filter(
        (stock) => stock.status === 1,
      );

      setStocks(activeStocks);
      setFilteredStocks(activeStocks);
    }
  }, [response]);

  useEffect(() => {
    if (
      watchlistResData &&
      Array.isArray(watchlistResData) &&
      stocks.length > 0
    ) {
      const watchlistStockIds = watchlistResData.map((item) => item.stock_id);

      const watchlistStocks = stocks.filter((stock) =>
        watchlistStockIds.includes(stock.id),
      );

      setSelectedSotcks(watchlistStocks);
    }
  }, [watchlistResData, stocks, setSelectedSotcks]);

  // Get unique stock exchanges from stocks
  const stockExchanges = useMemo(() => {
    const exchanges = new Set<string>();
    stocks?.forEach((stock) => {
      if (stock.market_name) {
        exchanges.add(stock.market_name);
      }
    });
    return Array.from(exchanges).sort();
  }, [stocks]);

  useEffect(() => {
    if (!stocks || stocks.length === 0) return;

    const filtered = stocks.filter((stock) => {
      // Filter by exchange
      if (
        selectedExchange !== "all" &&
        stock.market_name !== selectedExchange
      ) {
        return false;
      }

      if (!searchTerm) return true;

      const normalizedSearchTerm = searchTerm.toLowerCase();

      return (
        stock.ticker_symbol?.toLowerCase().includes(normalizedSearchTerm) ||
        stock.company_name?.toLowerCase().includes(normalizedSearchTerm)
      );
    });

    setFilteredStocks(filtered);
  }, [searchTerm, stocks, selectedExchange]);

  const isStockSelected = (stock: any): boolean => {
    const isInSelectedStocks = selectedStocks.some(
      (selectedStock) => selectedStock.id === stock.id,
    );

    const isInWatchlist =
      watchlistResData && Array.isArray(watchlistResData)
        ? watchlistResData.some(
            (watchlistItem) => watchlistItem.stock_id === stock.id,
          )
        : false;

    return isInSelectedStocks || isInWatchlist;
  };

  const handleStockClick = async (stock: any) => {
    if (watchlistLoading) return;

    const isSelected = isStockSelected(stock);

    try {
      if (isSelected) {
        updateWatchlist({ stock_id: stock.id, action: "remove" });

        const updatedStocks = selectedStocks.filter(
          (selectedStock) => selectedStock.id !== stock.id,
        );
        setSelectedSotcks(updatedStocks);
      } else {
        updateWatchlist({ stock_id: stock.id, action: "add" });

        setSelectedSotcks([...selectedStocks, stock]);
      }
    } catch (error) {
      console.error("Failed to update watchlist:", error);
    }
  };

  return (
    <div className="relative !w-full " ref={dropdownRef}>
      <div
        className={`
          vi-wl-control
          w-full !h-[40px] rounded-[10px]
          flex items-center gap-2 px-3
          ${isOpen ? "!border-[color:var(--vi-accent)]" : ""}
        `}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Search
          size={16}
          className="text-[color:var(--vi-text-tertiary)] shrink-0"
        />
        <input
          type="text"
          placeholder="Select and search stocks..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          className="w-full !outline-none custom_selcet !border-none !bg-transparent h-8 focus:!outline-none focus:!border-none
            text-[color:var(--vi-text)] placeholder:text-[color:var(--vi-text-tertiary)]"
        />
      </div>

      {isOpen && (
        <div
          className="
          vi-wl-stock-dropdown
          absolute top-full left-0 right-0 z-10
          mt-1
          rounded-[8px]
          max-h-80 overflow-y-auto
        "
        >
          {/* Exchange Filter Buttons */}
          <div className="vi-wl-exchange-bar p-3 sticky top-0 z-10">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedExchange("all");
                }}
                className={`
                  px-3 py-1.5 rounded-[9px] text-xs font-medium transition-all duration-200
                  ${
                    selectedExchange === "all"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }
                `}
              >
                All Exchanges
              </button>
              {stockExchanges.map((exchange) => {
                const { logo, short } = getExchangeInfo(exchange);
                return (
                  <button
                    key={exchange}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedExchange(exchange);
                    }}
                    className={`
                      px-3 py-1.5 rounded-[9px] text-xs font-medium transition-all duration-200
                      flex items-center gap-2
                      ${
                        selectedExchange === exchange
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }
                    `}
                  >
                    {logo && (
                      <img
                        src={logo}
                        alt={short}
                        className="w-4 h-4 object-cover"
                      />
                    )}
                    {short}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stock List */}
          <div className="max-h-60 overflow-y-auto">
            {stockLoading ? (
              <div className="p-3 text-center text-gray-500">Loading...</div>
            ) : filteredStocks.length === 0 ? (
              <div className="p-3 text-center text-gray-500">
                No stocks found
              </div>
            ) : (
              filteredStocks.map((stock) => (
                <div
                  key={`${stock.ticker_symbol}-${stock.value}-${
                    stock.id || Math.random()
                  }`}
                  className={`
                    px-3 py-2 
                    hover:bg-gray-100 
                    cursor-pointer 
                    flex justify-between items-center
                    ${watchlistLoading ? "opacity-50 cursor-not-allowed" : ""}
                  `}
                  onClick={() => handleStockClick(stock)}
                >
                  <div className="flex flex-col w-full">
                    <div className="w-full flex items-center justify-between">
                      <div className="flex items-center gap-x-2">
                        <img
                          src={`/${stock.company_logo}`}
                          alt={stock.company_logo}
                          className="w-6 h-6 object-contain"
                        />
                        <span className="font-dm text-sm text-[#344054]">
                          {stock.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-3">
                        <div className="flex items-center gap-x-2">
                          <img
                            src={marketLogoMap[stock?.market_name] ?? ""}
                            alt=""
                            className="w-6 h-6 object-cover"
                          />
                          <span className="text-[#344054] font-dm text-sm">
                            {stock.short_name}
                          </span>
                        </div>
                        <StarIcon
                          filled={isStockSelected(stock)}
                          className={`transition-colors duration-200 ${
                            watchlistLoading
                              ? "text-gray-300"
                              : isStockSelected(stock)
                                ? "text-yellow-500"
                                : "text-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
